import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTheme } from "../../../../context/ThemeContext";
import {
  Card,
  Button,
  Badge,
  Image,
  Modal,
  Dropdown,
  Spinner,
} from "react-bootstrap";
import {
  ThumbsUp,
  Send,
  Copy,
  Info,
  Trash2,
  Edit,
  ZoomIn,
  Repeat2,
  Bookmark,
  MessageSquare,
} from "lucide-react";
import { ReactComponent as ChevronRightIcon } from "../../../../assets/images/btn-right-aero.svg";

import { toast } from "sonner";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { rePost } from "../../../../redux/slices/communitySlice";
import usePlan from "../../../../hooks/usePlan";
import { FacebookSelector, FacebookCounter } from "@charkour/react-reactions";
import ArticleDetailModal from "../article-detail-modal/ArticleDetailModal";
import CommentsSection from "../comment-section/CommentsSection";
import MessageCircle from "../../../../assets/images/messagecircle.svg";
import { getUser } from "../../../../utils/auth";
import communityService from "../../../../api/communityService";
import {
  linkifyText,
  shortRole,
  shortSpecialty,
  categoryBg,
  sanitizePreviewContent,
  getVisibleTextLength,
} from "../../utils/communityHelpers";
import wowImg from "../../../../assets/images/Community_emojis/wow.png";
import hahaImg from "../../../../assets/images/Community_emojis/haha.png";
import loveImg from "../../../../assets/images/Community_emojis/Heart.png";
import angryImg from "../../../../assets/images/Community_emojis/angry.png";
import sadImg from "../../../../assets/images/Community_emojis/sad.png";
import Carousel from "react-bootstrap/Carousel";
import { MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import EditPost from "../edit-post/Edit-Post";
import { FiX } from "react-icons/fi";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
  FaInstagram,
} from "react-icons/fa6";
import ImageZoomModal from "../image-zoom-modal";
import UserProfileModal from "../user-profile-modal/UserProfileModal";
import RestrictionModal from "../../../restriction-modals/RestrictionModal";
import PollWidget from "../poll-widget/PollWidget";
import parse from "html-react-parser";
import Linkify from "linkify-react";
// import "../../styles/Community.scss";

// Cache for following list to avoid multiple API calls
let followingListCache = null;
let followingListCacheTime = null;
let followingListPromise = null; // Track ongoing API call
let lastPostsHash = null; // Track if posts have changed
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper function to create a hash of post user IDs
const createPostsHash = (posts) => {
  if (!posts || !Array.isArray(posts)) return "";
  const userIds = posts
    .map((post) => post._metadata?.userId)
    .filter(Boolean)
    .sort()
    .join(",");
  return userIds;
};

// Global event system for cache invalidation
window.invalidateFollowCache = () => {
  followingListCache = null;
  followingListCacheTime = null;
  followingListPromise = null;
  console.log("Follow cache invalidated due to new posts");
};

// Rotating avatar background colors (ui-avatars expects hex without "#")
const AVATAR_BG_PALETTE = [
  "0E2A47", // rgb(14, 42, 71)
  "7C3AED", // rgb(124, 58, 237)
  "0395D3",
  "DC2626", // rgb(220, 38, 38)
  "D97706", // rgb(217, 119, 6)
  "A02021", // rgb(160, 32, 33)
  "0E7C7B", // rgb(14, 124, 123)
  "16A34A", // rgb(22, 163, 74)
  "0E7C7B", // rgb(14, 124, 123)
];


const PostCard = ({
  post,
  cardIndex = 0,
  onLike,
  onReaction,
  onDelete,
  onReport,
  onCommentCountUpdate,
  onShowArticle,
  onShare,
  onHide,
  onSaveChange,
  hideRepost = false,
  refreshPosts,
  onOpenDetail,
}) => {
  // Pick a background color from the palette, looping when we run out
  const avatarBg = AVATAR_BG_PALETTE[cardIndex % AVATAR_BG_PALETTE.length];
  // ============= STATE =============
  const [showComments, setShowComments] = useState(false);
  const [showArticle, setShowArticleModal] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportReason, setSelectedReportReason] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const [index, setIndex] = useState(0);

  // Image zoom modal state
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);

  // Original post image zoom modal state
  const [showOriginalImageZoom, setShowOriginalImageZoom] = useState(false);
  const [originalZoomImageIndex, setOriginalZoomImageIndex] = useState(0);

  // Repost modal state
  const [showRepostModal, setShowRepostModal] = useState(false);

  // State for "See more" functionality
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOriginalPostExpanded, setIsOriginalPostExpanded] = useState(false);

  // Follow state — seeded from the posts API (post.isFollowing).
  // No need for a per-card /follow-status call.
  const [isFollowing, setIsFollowing] = useState(Boolean(post?.isFollowing));
  const [followLoading, setFollowLoading] = useState(false);
  const [checkingFollowStatus, setCheckingFollowStatus] = useState(false);

  // Unfollow confirmation modal state
  const [showUnfollowModal, setShowUnfollowModal] = useState(false);
  const [unfollowLoading, setUnfollowLoading] = useState(false);

  // User Profile Modal state
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [
    showOriginalUserProfileModal,
    setShowOriginalUserProfileModal,
  ] = useState(false);

  // Subscription restriction state
  const [showPaywallSheet, setShowPaywallSheet] = useState(false);

  // Save/bookmark restriction shown when a free user tries to save a post
  const [showSaveRestriction, setShowSaveRestriction] = useState(false);

  // Repost restriction shown when a free user tries to repost
  const [showRepostRestriction, setShowRepostRestriction] = useState(false);

  // Saved/Bookmark state — seeded from posts API (post.isSaved)
  const [isSaved, setIsSaved] = useState(Boolean(post?.isSaved));
  const [saveLoading, setSaveLoading] = useState(false);

  // Reactions modal state
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [selectedReactionUser, setSelectedReactionUser] = useState(null);
  const [
    showReactionUserProfileModal,
    setShowReactionUserProfileModal,
  ] = useState(false);

  // Get user profile from Redux store
  const store = useSelector((state) => state.profile);
  const user = useSelector((state) => state.auth?.user);

  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isPaid: isProUser, isFree } = usePlan();

  // Global user profile view count using localStorage (persists across sessions)
  const getUserProfileViewCount = () => {
    const count = localStorage.getItem("userProfileViewCount");
    return count ? parseInt(count, 10) : 0;
  };

  const incrementUserProfileViewCount = () => {
    const currentCount = getUserProfileViewCount();
    localStorage.setItem("userProfileViewCount", (currentCount + 1).toString());
  };

  const dispatch = useDispatch();
  const { resolvedTheme } = useTheme();
  const imageNotAvailableSrc =
    resolvedTheme === "dark"
      ? "data:image/svg+xml;base64," +
        btoa(
          '<svg width="600" height="400" xmlns="http://www.w3.org/2000/svg"><rect width="600" height="400" fill="#1a2830"/><text x="50%" y="50%" font-family="Arial, sans-serif" font-size="18" fill="#9aa7ac" text-anchor="middle" dominant-baseline="middle">Image Not Available</text></svg>',
        )
      : "data:image/svg+xml;base64," +
        btoa(
          '<svg width="600" height="400" xmlns="http://www.w3.org/2000/svg"><rect width="600" height="400" fill="#e5e7eb"/><text x="50%" y="50%" font-family="Arial, sans-serif" font-size="18" fill="#6b7280" text-anchor="middle" dominant-baseline="middle">Image Not Available</text></svg>',
        );

  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  const navigate = useNavigate();

  // Local state for optimistic updates (synced with parent) - MOVED UP
  const [localPost, setLocalPost] = useState(post);

  // Check if current user owns this post - MOVED UP to avoid reference error
  const currentUser = getUser();
  const currentUserId = currentUser?.id;
  const isOwnPost = localPost._metadata?.userId === currentUserId;

  // Deleted user: post has no user_id and is not anonymous.
  // Show "Deleted User", suppress profile modal, and hide follow button.
  const isDeletedUser =
    !localPost._metadata?.userId && localPost._metadata?.isAnonymous !== 1;

  // const formattedContent = useMemo(() => {
  //   return linkifyText(post.content || "");
  // }, [post.content]);
  const formattedContent = useMemo(() => {
    return (post.content || "").split(/\r?\n/);
  }, [post.content]);

  // Reaction picker state (cosmetic only - all reactions become simple "like")
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [hoverHideTimer, setHoverHideTimer] = useState(null);

  const editValidityTime = new Date(localPost?._metadata?.createdAt);

  const now = new Date();
  const diffMs = now - editValidityTime; // gives milliseconds difference
  const diffMinutes = diffMs / (1000 * 60); // convert to minutes

  const getPreviewUrl = (preview) => {
    if (!preview) return null;

    // If backend already sends full url, use it
    if (preview.url) {
      return preview.url;
    }

    // Fallback: build from domain
    if (preview.domain) {
      return `https://${preview.domain.replace(/^https?:\/\//, "")}`;
    }

    return null;
  };
  const hasValidLinkPreview = (preview) => {
    if (!preview) return false;

    return Boolean(
      preview.image?.trim() ||
        preview.title?.trim() ||
        preview.description?.trim() ||
        preview.domain?.trim(),
    );
  };
  const getFirstLinkFromContent = (content = "") => {
    const div = document.createElement("div");
    div.innerHTML = content;
    const plainText = div.textContent || div.innerText || "";
    const urlRegex = /(https?:\/\/[^\s]+)/i;
    const match = plainText.match(urlRegex);
    return match ? match[0] : null;
  };
  const contentLink = useMemo(
    () => getFirstLinkFromContent(localPost?.content),
    [localPost?.content],
  );

  /**
   * Sync local state with prop changes
   * Needed when parent updates post data
   */
  useEffect(() => {
    // Debug optimistic posts
    if (post?._optimistic) {
    }
    setLocalPost(post);
  }, [post]);
  const reportReasons = [
    "Spam",
    "Nudity",
    "Scam",
    "Hate Speech",
    "Harassment",
    "Violence",
    "Misinformation",
    "Terrorism",
    "Illegal Activity",
    "Child Exploitation",
  ];
  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ============= HANDLERS =============
  /**
   * Handle like/unlike with reaction API
   * Now uses the reaction system instead of old like endpoint
   * Clicking the blue Like button = adding/removing 'like' reaction
   */
  const handleLike = async () => {
    if (!localPost?.id) return;

    // If user already has ANY reaction, clicking Like removes it
    // If user has no reaction, clicking Like adds 'like' reaction
    if (onReaction) {
      await onReaction(localPost.id, "like");
    } else {
      console.error("❌ [LIKE BUTTON] onReaction prop not defined");
    }
  };

  /**
   * Handle emoji reaction pick
   * Calls the real reaction API and updates UI based on response
   */
  const handleReactionPick = async (reaction) => {
    if (!localPost?.id) {
      console.warn("⚠️ [POST CARD] No post ID available");
      return;
    }

    setShowReactionPicker(false);

    // Call the parent's onReaction handler (connected to API)
    if (onReaction) {
      await onReaction(localPost.id, reaction);
    } else {
      console.error("❌ [POST CARD] onReaction prop is not defined!");
    }
  };

  /**
   * Handle reaction picker hover
   */
  const handleReactionsEnter = () => {
    if (hoverHideTimer) {
      clearTimeout(hoverHideTimer);
    }
    setShowReactionPicker(true);
  };

  const handleReactionsLeave = () => {
    const t = setTimeout(() => setShowReactionPicker(false), 250);
    setHoverHideTimer(t);
  };

  /**
   * Toggle comments section visibility
   */
  const toggleComments = () => {
    setShowComments(!showComments);
  };

  /**
   * Handle comment count updates from CommentsSection
   */
  const handleCommentUpdate = useCallback(
    (newCount) => {
      // Update local state
      setLocalPost((prevPost) => ({
        ...prevPost,
        comments: newCount,
      }));

      // Notify parent
      if (onCommentCountUpdate && localPost?.id) {
        onCommentCountUpdate(localPost.id, newCount);
      }
    },
    [localPost?.id, onCommentCountUpdate],
  );

  /**
   * Open article detail modal
   */
  const openArticle = () => {
    if (onShowArticle) {
      onShowArticle(localPost);
    } else {
      setShowArticleModal(true);
    }
  };

  const closeArticle = () => {
    setShowArticleModal(false);
  };

  /**
   * Get the post author's user id (used for follow API)
   */
  const postAuthorId = localPost?._metadata?.userId;
  const canFollow =
    !!postAuthorId &&
    !isOwnPost &&
    localPost?._metadata?.isAnonymous !== 1 &&
    !localPost?._optimistic;

  /**
   * Keep local follow state in sync with the post prop.
   * `is_following` now comes from the posts list API (mapped to `post.isFollowing`),
   * so no per-card /follow-status request is required.
   */
  useEffect(() => {
    if (!canFollow) {
      setIsFollowing(false);
      return;
    }
    setIsFollowing(Boolean(post?.isFollowing));
  }, [post?.isFollowing, canFollow]);

  /**
   * Listen for follow/unfollow events from other PostCard instances by the same user.
   * Keeps the Follow/Following state in sync across all cards on the page.
   */
  useEffect(() => {
    if (!postAuthorId) return undefined;
    const handler = (event) => {
      const { userId, isFollowing: nextIsFollowing } = event.detail || {};
      if (userId === postAuthorId) {
        setIsFollowing(!!nextIsFollowing);
      }
    };
    window.addEventListener("community:follow-change", handler);
    return () => {
      window.removeEventListener("community:follow-change", handler);
    };
  }, [postAuthorId]);

  /**
   * Follow button click handler.
   * - When not following: call follow API directly.
   * - When already following: open the Unfollow confirmation modal.
   */
  const handleFollowToggle = async (e) => {
    if (e) e.stopPropagation();
    if (!canFollow || followLoading) return;

    // Unfollow is always allowed (no gate) — opens the confirmation modal.
    if (isFollowing) {
      setShowUnfollowModal(true);
      return;
    }

    // Free-user gate: free users cannot follow anyone. Every follow attempt
    // opens the subscription/upgrade modal — same UX as the profile-view
    // restriction.
    if (!isProUser) {
      setShowPaywallSheet(true);
      return;
    }

    setFollowLoading(true);
    try {
      const response = await communityService.follow(postAuthorId);
      if (response?.data?.success) {
        setIsFollowing(true);
        // Broadcast so other PostCard instances by the same author update too
        window.dispatchEvent(
          new CustomEvent("community:follow-change", {
            detail: { userId: postAuthorId, isFollowing: true },
          }),
        );
        toast.success(response?.data?.message || "Followed successfully");
      }
    } catch (err) {
      console.error("Error following user:", err);
      toast.error(err?.response?.data?.message || "Failed to follow user");
    } finally {
      setFollowLoading(false);
    }
  };

  /**
   * Confirm unfollow — same API as FollowingModal (communityService.unfollow)
   */
  const confirmUnfollow = async () => {
    if (!postAuthorId) return;
    setUnfollowLoading(true);
    try {
      const response = await communityService.unfollow(postAuthorId);
      if (response?.data?.success) {
        setIsFollowing(false);
        // Broadcast so other PostCard instances by the same author update too
        window.dispatchEvent(
          new CustomEvent("community:follow-change", {
            detail: { userId: postAuthorId, isFollowing: false },
          }),
        );
        toast.success(
          response?.data?.message ||
            `Unfollowed ${localPost.author || "user"} successfully`,
        );
        setShowUnfollowModal(false);
      }
    } catch (err) {
      console.error("Error unfollowing user:", err);
      toast.error("Failed to unfollow user");
    } finally {
      setUnfollowLoading(false);
    }
  };

  const cancelUnfollow = () => {
    setShowUnfollowModal(false);
  };

  /**
   * Keep local saved state in sync with the post prop (from posts API)
   */
  useEffect(() => {
    setIsSaved(Boolean(post?.isSaved));
  }, [post?.isSaved]);

  /**
   * Handle save/bookmark toggle — calls /frontend/post-saves/toggle
   */
  const handleSaveClick = async () => {
    if (!localPost?.id || saveLoading || localPost._optimistic) return;

    // Free users cannot save posts — show the upgrade modal. Un-saving an
    // already-saved post is still allowed for everyone.
    if (!isProUser && !isSaved) {
      setShowSaveRestriction(true);
      return;
    }

    const previous = isSaved;
    const next = !previous;
    setIsSaved(next); // optimistic update
    setSaveLoading(true);

    try {
      const response = await communityService.togglePostSave(localPost.id);
      if (response?.data?.success) {
        toast.success(
          response?.data?.message ||
            (next ? "Post saved" : "Removed from saved"),
        );
        // Notify the parent so the Saved-tab list can drop a post the user
        // just un-saved (otherwise it sticks around until refresh).
        if (typeof onSaveChange === "function") {
          onSaveChange(localPost.id, next);
        }
      } else {
        // revert if backend reports failure
        setIsSaved(previous);
        toast.error(response?.data?.message || "Failed to update save");
      }
    } catch (err) {
      console.error("Error toggling post save:", err);
      setIsSaved(previous);
      toast.error(err?.response?.data?.message || "Failed to update save");
    } finally {
      setSaveLoading(false);
    }
  };

  /**
   * Handle share modal
   */
  const handleShareClick = () => {
    if (onShare) {
      onShare(localPost.id);
    } else {
      setShareModal(true);
    }
  };

  /**
   * Handle user profile click with subscription restriction
   */
  const handleUserProfileClick = (e) => {
    e.stopPropagation();

    // Don't open modal for anonymous posts, own posts, or deleted users
    if (localPost._metadata.isAnonymous === 1 || isOwnPost || isDeletedUser) {
      return;
    }

    // Free users cannot open the profile modal at all — show the upgrade modal
    if (isFree) {
      setShowPaywallSheet(true);
      return;
    }

    setShowUserProfileModal(true);
  };

  /**
   * Handle original post user profile click with subscription restriction
   */
  const handleOriginalUserProfileClick = (e) => {
    e.stopPropagation();

    // Don't open modal for own posts
    if (localPost.original_post?.user?.id === currentUserId) {
      return;
    }

    // Free users cannot open the profile modal at all — show the upgrade modal
    if (isFree) {
      setShowPaywallSheet(true);
      return;
    }

    setShowOriginalUserProfileModal(true);
  };

  /**
   * Handle reaction user profile click with subscription restriction
   */
  const handleReactionUserClick = (user) => {
    // Don't open modal for own profile
    if (user.id === currentUserId) {
      return;
    }

    // Only free users are subject to the profile-view gate
    if (isFree) {
      const viewCount = getUserProfileViewCount();

      // Allow first view for free
      if (viewCount >= 1) {
        // Show subscription modal on second attempt
        setShowPaywallSheet(true);
        return;
      }
      // Increment global view count
      incrementUserProfileViewCount();
    }

    // Set selected user and show modal
    setSelectedReactionUser(user);
    setShowReactionUserProfileModal(true);
  };

  const closeShareModal = () => {
    setShareModal(false);
  };

  /**
   * Handle delete post
   */
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const confirmDeletePost = async () => {
    try {
      setShowDeleteModal(false);
      if (onDelete) {
        await onDelete(localPost.id);
      }
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  const cancelDeletePost = () => {
    setShowDeleteModal(false);
  };

  /**
   * Handle report post
   */
  const handleReportClick = () => {
    setShowReportModal(true);
    setOpen(false); // Close the menu
  };

  const handleReportReasonChange = (reason) => {
    setSelectedReportReason(reason);
  };

  const confirmReportPost = async () => {
    if (!selectedReportReason) {
      toast.error("Please select a reason for reporting");
      return;
    }

    try {
      setShowReportModal(false);
      if (onReport) {
        await onReport(localPost.id, selectedReportReason);
      }
      setSelectedReportReason(""); // Reset selection
    } catch (error) {
      console.error("Error reporting post:", error);
    }
  };

  const cancelReportPost = () => {
    setShowReportModal(false);
    setSelectedReportReason(""); // Reset selection
  };

  /**
   * Share to social media platforms
   */
  const shareToSocialMedia = (platform) => {
    const shareText = `Check out this post by ${
      localPost?.author
    }: ${localPost?.content?.substring(0, 100) || ""}...`;
    const shareUrl = window.location.href;
    // console.log("clicked")
    let url = "";
    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText,
        )}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl,
        )}`;
        break;
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(
          shareText + " " + shareUrl,
        )}`;
        break;
      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          shareUrl,
        )}`;
        break;
      case "instagram":
        // Instagram doesn't have a web share API, so we copy the text + link
        // and try to open Instagram (mobile will open app, desktop opens web)
        const instagramText = `${shareText}\n\n${shareUrl}`;
        navigator.clipboard
          .writeText(instagramText)
          .then(async () => {
            closeShareModal();
            await Swal.fire({
              icon: "success",
              title: "Content Copied!",
              text:
                "Post content and link copied to clipboard. You can now paste it on Instagram!",
              timer: 2000,
              showConfirmButton: false,
              customClass: { popup: "success-popup-modal" },
            });
            // After Swal closes, open Instagram
            window.open("https://www.instagram.com/", "_blank");
          })
          .catch(() => {
            toast.error("Failed to copy content");
          });
        return;
      case "copy":
        navigator.clipboard
          .writeText(shareUrl)
          .then(() => {
            toast.success("Link copied to clipboard!");
          })
          .catch(() => {
            toast.error("Failed to copy link");
          });
        closeShareModal();
        return;
      default:
        return;
    }

    if (url) {
      const popup = window.open(
        url,
        "_blank",
        "width=600,height=400,scrollbars=yes,resizable=yes",
      );
      if (!popup) {
        toast.warning("Please allow popups for this site to share content");
      }
      closeShareModal();
    }
  };

  // ============= SAFETY CHECKS =============
  if (!localPost) {
    return null;
  }

  // When the parent provides onOpenDetail, open the post in an in-place modal
  // (keeps the feed mounted → preserves scroll, no refetch). Otherwise fall
  // back to navigating to the standalone /community/:id detail page.
  const navigateToPostDetails = () => {
    if (onOpenDetail) {
      onOpenDetail(localPost.id, localPost.plan_name);
      return;
    }
    navigate(`/community/${localPost.id}`, {
      state: { comment_id: parseInt(localPost?.id), userinfo: localPost },
    });
  };

  const navigateToPostDetailsFromComment = () => {
    if (onOpenDetail) {
      onOpenDetail(localPost.id, localPost.plan_name);
      return;
    }
    navigate(`/community/${localPost.id}`, {
      state: { comment_id: parseInt(localPost?.id), userinfo: localPost },
    });
  };
  const reactionIcons = {
    like: (
      <ThumbsUp
        height={22}
        fill={localPost.userReaction ? "currentColor" : "none"}
      />
    ),
    love: <img src={loveImg} alt="love" width={24} height={22} />,
    haha: <img src={hahaImg} alt="haha" width={24} height={22} />,
    wow: <img src={wowImg} alt="wow" width={24} height={22} />,
    sad: <img src={sadImg} alt="sad" width={24} height={22} />,
    angry: <img src={angryImg} alt="angry" width={24} height={22} />,
  };

  const handleEditClick = () => {
    setShowEditModal(true);
    setOpen(false);
  };

  /**
   * Handle repost button click
   */
  const handleRepostClick = () => {
    if (!isProUser) {
      setShowRepostRestriction(true);
      return;
    }
    setShowRepostModal(true);
  };

  /**
   * Handle repost submission
   */
  const handleRepostSubmit = async () => {
    try {
      const result = await dispatch(
        rePost({
          original_post_id: localPost.id,
        }),
      ).unwrap();

      if (result?.success) {
        toast.success(result?.message || "Post reposted successfully!");
        setShowRepostModal(false);

        // Refresh posts if callback provided
        if (refreshPosts) {
          refreshPosts();
        }

        // Refresh the entire page and scroll to top
        setTimeout(() => {
          window.scrollTo(0, 0);
          window.location.reload();
        }, 1000); // Small delay to show the success message
      }
    } catch (error) {
      console.error("Error reposting:", error);
      toast.error(error?.message || "Failed to repost");
    }
  };

  /**
   * Handle repost modal close
   */
  const handleRepostCancel = () => {
    setShowRepostModal(false);
  };

  const FreeIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24">
      <path
        d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z"
        fill="#B9C2CD"
      />
      <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
      <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
    </svg>
  );

  const ProIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24">
      <path
        d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z"
        fill="#0395D3"
      />
      <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
      <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
    </svg>
  );

  const NewsletterIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24">
      <path
        d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z"
        fill="#C8941C"
      />
      <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
      <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
    </svg>
  );

  const PlusIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24">
      <path
        d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z"
        fill="#16A34A"
      />
      <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
      <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
    </svg>
  );

  const hasHtml =
  typeof localPost.content === "string" &&
  /<\/?[a-z][\s\S]*>/i.test(localPost.content);

  // ============= RENDER =============
  return (
    <>
      <Card
        className="posts-feeds-card"
        data-tour={cardIndex === 0 ? "cm-postcard" : undefined}
      >
        {/* Repost Indicator */}
        {localPost.is_repost && (
          <div
            style={{
              padding: "8px 16px",
              borderBottom: "1px solid var(--mmp-border)",
              color: "var(--mmp-text-secondary)",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Repeat2 size={16} />
            <span>
              <strong>
                {(() => {
                  const currentUser = getUser();
                  const isCurrentUser =
                    currentUser?.id === localPost._metadata?.userId;
                  return isCurrentUser ? "You" : localPost.author;
                })()}
              </strong>{" "}
              {(() => {
                const currentUser = getUser();
                const isCurrentUser =
                  currentUser?.id === localPost._metadata?.userId;
                return isCurrentUser ? "have" : "has";
              })()}{" "}
              reposted this post
            </span>
          </div>
        )}
        {/* Post Header */}
        <div className="post-card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center user-profile">
              <Image
                // src={
                //   localPost._metadata.isAnonymous === 1
                //     ? `https://ui-avatars.com/api/?name=Anonymous&background=0195D3&color=fff&size=42`
                //     : localPost.authorImage
                //     ? localPost.authorImage
                //     : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                //         localPost.author || "User"
                //       )}&background=0195D3&color=fff&size=42`
                // }
                src={
                  localPost._metadata.isAnonymous === 1
                    ? `https://ui-avatars.com/api/?name=Anonymous&background=0195D3&color=fff&size=42`
                    : isDeletedUser
                    ? `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        "Deleted User",
                      )}&background=9CA3AF&color=fff&size=42`
                    : localPost.authorImage
                    ? `${process.env.REACT_APP_API_BASE_URL}/${
                        localPost.authorImage.startsWith(
                          "uploads/user_profile/",
                        )
                          ? localPost.authorImage
                          : `uploads/user_profile/${localPost.authorImage}`
                      }`
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        localPost.author || "User",
                      )}&background=${avatarBg}&color=fff&size=42`
                }
                alt={
                  localPost._metadata.isAnonymous === 1
                    ? "Anonymous user"
                    : isDeletedUser
                    ? "Deleted User"
                    : localPost.author || "User"
                }
                roundedCircle
                width={39}
                height={39}
                className="user-image"
                style={{
                  objectFit: "cover",
                  cursor:
                    localPost._metadata.isAnonymous !== 1 &&
                    !isOwnPost &&
                    !isDeletedUser
                      ? "pointer"
                      : "default",
                }}
                onClick={handleUserProfileClick}
                onError={(e) => {
                  e.target.onerror = null;
                  const fallbackBg =
                    localPost._metadata.isAnonymous === 1
                      ? "0195D3"
                      : isDeletedUser
                      ? "9CA3AF"
                      : avatarBg;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    localPost._metadata.isAnonymous === 1
                      ? "Anonymous"
                      : isDeletedUser
                      ? "Deleted User"
                      : localPost.author || "User",
                  )}&background=${fallbackBg}&color=fff&size=42`;
                }}
              />

              <div
                className="user-content"
                data-tour={cardIndex === 0 ? "cm-pc-profile" : undefined}
              >
                <div
                  className="user-name"
                  style={{
                    cursor:
                      localPost._metadata.isAnonymous !== 1 &&
                      !isOwnPost &&
                      !isDeletedUser
                        ? "pointer"
                        : "default",
                  }}
                  onClick={handleUserProfileClick}
                >
                  {localPost._metadata.isAnonymous === 1
                    ? "Anonymous"
                    : isDeletedUser
                    ? "Deleted User"
                    : localPost.author}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {localPost.plan_name === "Free" && <FreeIcon />}

                    {localPost.plan_name === "Plus" && <PlusIcon />}

                    {(localPost.plan_name === "Pro" || localPost.plan_name === "Pro Trial") && <ProIcon />}

                    {localPost.plan_name?.toLowerCase() ===
                      "pro + newsletter" && <NewsletterIcon />}
                  </span>
                </div>
                {localPost._metadata.isAnonymous !== 1 &&
                  !isDeletedUser &&
                  (() => {
                    const meta = localPost?._metadata || {};
                    const parts = [
                      shortRole(meta.userRole),
                      shortSpecialty(meta.userPrimarySpecialty),
                      meta.userCity,
                    ].filter(Boolean);

                    if (parts.length === 0) {
                      // Fall back to the legacy state-only line so older
                      // payloads that don't include role/specialty/city
                      // still render something.
                      return meta.userState ? (
                        <div className="user-role">{meta.userState}</div>
                      ) : null;
                    }
                    return <div className="user-role">{parts.join(" · ")}</div>;
                  })()}
              </div>
            </div>

            <div className="d-flex align-items-center">
              {/* Follow Button — shows for other users' posts (non-anonymous, non-own) */}
              {canFollow && (
                <button
                  type="button"
                  className={`btn-follow ${isFollowing ? "is-following" : ""}`}
                  data-tour={cardIndex === 0 ? "cm-pc-follow" : undefined}
                  onClick={handleFollowToggle}
                  disabled={followLoading || checkingFollowStatus}
                >
                  {followLoading ? "..." : isFollowing ? "Following" : "Follow"}
                </button>
              )}

              {/* Delete Button - Only show for own posts */}
              {isOwnPost && !localPost._optimistic && (
                <div ref={menuRef} className="menu-wrapper ms-1">
                  <Dropdown
                    align="end"
                    show={open}
                    onToggle={() => setOpen((prev) => !prev)}
                  >
                    <Dropdown.Toggle
                      as={Button}
                      variant="link"
                      className="p-0 rounded-none bg-transparent btn-more-vertical"
                      id={`post-actions-${localPost.id}`}
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="p-0">
                      {/* Edit options */}
                      {diffMinutes < 180 ? (
                        <>
                          <Dropdown.Item
                            className="d-flex align-items-center gap-2 text-primary"
                            onClick={handleEditClick}
                          >
                            <Edit size={18} />
                            Edit post
                          </Dropdown.Item>
                        </>
                      ) : (
                        <></>
                      )}
                      {/* Delete option */}
                      <Dropdown.Item
                        className="d-flex align-items-center gap-2 text-danger"
                        onClick={handleDeleteClick}
                      >
                        <Trash2 size={18} />
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                    {/* Conditionally render the edit post modal */}
                  </Dropdown>
                </div>
              )}
              {showEditModal && (
                <EditPost
                  onPostUpdated={refreshPosts}
                  show={showEditModal}
                  onHide={() => setShowEditModal(false)}
                  // updatePost={updatePost} // ✅ Function that calls your API to update the post
                  localPost={localPost} // ✅ Existing post data (id, content, category, etc.)
                />
              )}
            </div>
          </div>

          {(localPost.categoryTag || localPost.time) && (
            <div className="post-meta-row d-flex justify-content-between align-items-center">
              {localPost.categoryTag ? (
                <div className="category-tags">
                  <Badge
                    bg=""
                    className="tag"
                    style={{
                      backgroundColor: categoryBg(localPost.categoryTag),
                      color: "#364152",
                      // color: "#6b7280",
                      // color: "#0E1726",
                    }}
                  >
                    {localPost.categoryTag}
                  </Badge>
                </div>
              ) : (
                <span />
              )}
              <div className="post-time">{localPost.time || "Just now"}</div>
            </div>
          )}
        </div>
        {/* ✅ TITLE */}
        {!localPost?.is_repost && localPost?.title && (
          <div className="post-title" onClick={() => navigateToPostDetails()}>
            <div className="post-text">{localPost.title}</div>
          </div>
        )}
        {/* ✅ DESCRIPTION (moved above the image — order is now title → desc → image) */}
        {!localPost?.is_repost && (
          <div className="post-content">
            <div className="post-text">
              <div
                style={{
                  display: isExpanded ? "block" : "-webkit-box",
                  WebkitLineClamp: isExpanded ? "unset" : 3,
                  WebkitBoxOrient: "vertical",
                  overflow: isExpanded ? "visible" : "hidden",
                  cursor: "pointer",
                }}
                onClick={() => navigateToPostDetails()}
              >
                {/* {formattedContent.map((line, lineIndex) => {
                  const isBullet = /^(\s*[-•]|\s*\d+\.)/.test(line);
                  const isHeading = /:$/.test(line.trim());

                  return (
                    <div
                      key={lineIndex}
                      style={{
                        marginTop: isHeading ? "10px" : "4px",
                        paddingLeft: isBullet ? "24px" : "0px",
                        textIndent: isBullet ? "-12px" : "0px",
                        fontWeight: isHeading ? 600 : "normal",
                      }}
                    >
                      {linkifyText(line).map((part, index) =>
                        part.type === "link" ? (
                          <a
                            key={index}
                            href={part.href}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (part.href.includes("/jobs/")) {
                                e.preventDefault();
                                navigate(
                                  part.href.replace(window.location.origin, ""),
                                );
                              }
                            }}
                            target={
                              part.href.includes("/jobs/") ? "_self" : "_blank"
                            }
                            rel="noopener noreferrer"
                            style={{
                              color: "var(--mmp-accent)",
                              textDecoration: "underline",
                              wordBreak: "break-word",
                            }}
                          >
                            {part.content}
                          </a>
                        ) : (
                          <span key={index}>{part.content}</span>
                        ),
                      )}
                    </div>
                  );
                })} */}

                {hasHtml ? (
                  <Linkify>
                    <div className="rich-content">
                      {parse(sanitizePreviewContent(localPost.content))}
                    </div>
                  </Linkify>
                ) : (
                  formattedContent.map((line, lineIndex) => {
                    const isBullet = /^(\s*[-•]|\s*\d+\.)/.test(line);
                    const isHeading = /:$/.test(line.trim());

                    return (
                      <div
                        key={lineIndex}
                        style={{
                          marginTop: isHeading ? "10px" : "4px",
                          paddingLeft: isBullet ? "24px" : "0px",
                          textIndent: isBullet ? "-12px" : "0px",
                          fontWeight: isHeading ? 600 : "normal",
                        }}
                      >
                        {linkifyText(line).map((part, index) =>
                          part.type === "link" ? (
                            <a
                              key={index}
                              href={part.href}
                              onClick={(e) => {
                                e.stopPropagation();

                                if (part.href.includes("/jobs/")) {
                                  e.preventDefault();
                                  navigate(
                                    part.href.replace(
                                      window.location.origin,
                                      "",
                                    ),
                                  );
                                }
                              }}
                              target={
                                part.href.includes("/jobs/")
                                  ? "_self"
                                  : "_blank"
                              }
                              rel="noopener noreferrer"
                              style={{
                                color: "var(--mmp-accent)",
                                textDecoration: "underline",
                                wordBreak: "break-word",
                              }}
                            >
                              {part.content}
                            </a>
                          ) : (
                            <span key={index}>{part.content}</span>
                          ),
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* See more / See less */}
              {(() => {
                const hasLongContent =
                  getVisibleTextLength(localPost.content) > 150 ||
                  formattedContent.length > 3;

                return (
                  hasLongContent && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                      }}
                      style={{
                        color: "var(--mmp-accent)",
                        cursor: "pointer",
                        fontWeight: 500,
                        marginTop: "8px",
                        fontSize: "14px",
                      }}
                    >
                      {isExpanded ? "See less" : "See more"}
                    </div>
                  )
                );
              })()}

              {hasValidLinkPreview(localPost?.link_preview) && (
                <div
                  className="link-preview-card mt-2 p-2 border rounded position-relative"
                  role="button"
                  tabIndex={0}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    window.open(contentLink, "_blank", "noopener,noreferrer");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      window.open(contentLink, "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  <div className="d-flex gap-2">
                    {localPost.link_preview.image && (
                      <Image
                        src={localPost.link_preview.image}
                        width={80}
                        height={80}
                        className="rounded"
                        style={{ objectFit: "cover" }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <div className="flex-grow-1">
                      {localPost.link_preview.title && (
                        <h6 className="mb-1 text-truncate">
                          {localPost.link_preview.title}
                        </h6>
                      )}
                      {localPost.link_preview.description && (
                        <p className="mb-1 text-muted small line-clamp-2">
                          {localPost.link_preview.description}
                        </p>
                      )}
                      {localPost.link_preview.domain && (
                        <small className="text-primary">
                          {localPost.link_preview.domain}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Embedded Poll */}
        {localPost.poll && (
          <PollWidget poll={localPost.poll} />
        )}

        {/* Post Image */}
        {localPost.image ? (
          localPost?.post_images?.length > 1 ? (
            <div
              className="post-image"
              style={{ cursor: "pointer", position: "relative" }}
            >
              <button
                className="image-zoom-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomImageIndex(index);
                  setShowImageZoom(true);
                }}
                title="Click to zoom"
                // style={{
                //   position: 'absolute',
                //   top: '12px',
                //   right: '12px',
                //   zIndex: 1000,
                //   background: 'rgba(0, 0, 0, 0.8)',
                //   border: '2px solid white',
                //   color: 'white',
                //   padding: '10px',
                //   borderRadius: '8px',
                //   cursor: 'pointer',
                //   display: 'flex',
                //   alignItems: 'center',
                //   justifyContent: 'center'
                // }}
              >
                <ZoomIn size={20} />
              </button>
              <Carousel
                slide={false}
                activeIndex={index}
                indicators={false}
                interval={null}
                touch={true}
                onSelect={handleSelect}
                className="w-100 h-100"
              >
                {localPost.post_images.map((img, index) => (
                  <Carousel.Item key={index} className="h-100">
                    <Image
                      key={resolvedTheme}
                      onClick={() => navigateToPostDetails()}
                      src={`${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`}
                      alt="Post content"
                      fluid
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          imageNotAvailableSrc;
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            </div>
          ) : (
            <div
              className="post-image"
              style={{ cursor: "pointer", position: "relative" }}
            >
              <button
                className="image-zoom-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomImageIndex(0);
                  setShowImageZoom(true);
                }}
                title="Click to zoom"
                // style={{
                //   position: "absolute",
                //   top: "12px",
                //   right: "12px",
                //   zIndex: 1000,
                //   background: "rgba(0, 0, 0, 0.8)",
                //   border: "2px solid white",
                //   color: "white",
                //   padding: "10px",
                //   borderRadius: "8px",
                //   cursor: "pointer",
                //   display: "flex",
                //   alignItems: "center",
                //   justifyContent: "center",
                // }}
              >
                <ZoomIn size={20} />
              </button>
              {localPost.post_images.map((img, index) => (
                <Image
                  key={`${index}-${resolvedTheme}`}
                  onClick={() => navigateToPostDetails()}
                  src={`${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`}
                  alt="Post content"
                  fluid
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      imageNotAvailableSrc;
                  }}
                />
              ))}
            </div>
          )
        ) : (
          ""
        )}
        {/* Post Content (description moved above — this wrapper now starts
            with the repost-original block / reactions / actions / comments) */}
        <div className="post-content">
          {/* Original Post Display */}
          {localPost.is_repost && localPost.original_post && (
            <div
              style={{
                margin: "6px",
                padding: "12px",
                border: "1px solid var(--mmp-accent-border)",
                borderRadius: "10px",
                backgroundColor: "var(--mmp-accent-bg)",
                cursor: "pointer",
              }}
              onClick={() => navigateToPostDetails()}
            >
              <div
                className="post-card-header d-flex"
                style={{
                  padding: "0 1px 6px",
                }}
              >
                <div className="d-flex align-items-center user-profile">
                  <Image
                    src={
                      localPost.original_post.user?.image
                        ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${localPost.original_post.user.image}`
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            localPost.original_post.user?.name || "User",
                          )}&background=0195D3&color=fff&size=42`
                    }
                    roundedCircle
                    width={39}
                    height={39}
                    className="user-image"
                    style={{
                      objectFit: "cover",
                      cursor:
                        localPost.original_post?.user?.id !== currentUserId
                          ? "pointer"
                          : "default",
                    }}
                    onClick={handleOriginalUserProfileClick}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        localPost.original_post.user?.name || "User",
                      )}&background=0195D3&color=fff&size=42`;
                    }}
                  />
                  <div className="user-content">
                    <div
                      className="user-name"
                      style={{
                        cursor:
                          localPost.original_post?.user?.id !== currentUserId
                            ? "pointer"
                            : "default",
                      }}
                      onClick={handleOriginalUserProfileClick}
                    >
                      {localPost.original_post.user?.name || "Unknown User"}
                      {localPost.original_post.user?.state &&
                        `, ${localPost.original_post.user.state}`}
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {(() => {
                          const origPlan =
                            localPost.original_post.user?.plan?.name ||
                            localPost.original_post.plan_name;
                          if (origPlan === "Free") return <FreeIcon />;
                          if (origPlan === "Plus") return <PlusIcon />;
                          if (origPlan === "Pro" || origPlan === "Pro Trial")
                            return <ProIcon />;
                          if (origPlan?.toLowerCase() === "pro + newsletter")
                            return <NewsletterIcon />;
                          return null;
                        })()}
                      </span>
                    </div>
                    <div className="post-time">
                      {new Date(
                        localPost.original_post.created_at,
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                  {/* <div className="ms-2">
                    <div style={{ fontWeight: 600, fontSize: "15px" }}>
                      {localPost.original_post.user?.name || "Unknown User"}
                      {localPost.original_post.user?.state &&
                        `, ${localPost.original_post.user.state}`}
                    </div>
                    <div style={{ fontSize: "13px", color: "#6b7280" }}>
                      {new Date(
                        localPost.original_post.created_at,
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div> */}
                </div>
              </div>

              {/* Original Post Title */}
              {localPost.original_post.title && (
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: "16px",
                    marginBottom: "8px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToPostDetails();
                  }}
                >
                  {localPost.original_post.title}
                </div>
              )}
              {/* Original Post Content */}
              {localPost.original_post.content && (
                <>
                  <div
                    style={{
                      fontSize: "14px",
                      whiteSpace: "pre-wrap",
                      cursor: "pointer",
                      display: isOriginalPostExpanded ? "block" : "-webkit-box",
                      WebkitLineClamp: isOriginalPostExpanded ? "unset" : 3,
                      WebkitBoxOrient: "vertical",
                      overflow: isOriginalPostExpanded ? "visible" : "hidden",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToPostDetails();
                    }}
                  >
                    {(() => {
                      const originalContent = localPost.original_post.content || "";
                      const originalHasHtml = /<\/?[a-z][\s\S]*>/i.test(originalContent);
                      if (originalHasHtml) {
                        return (
                          <Linkify>
                            <div className="rich-content" style={{ background: "transparent", wordBreak: "break-word", overflowWrap: "break-word" }}>
                              {parse(sanitizePreviewContent(originalContent))}
                            </div>
                          </Linkify>
                        );
                      }
                      return originalContent.split(/\r?\n/).map((line, lineIndex) => (
                        <div
                          key={lineIndex}
                          style={{ 
                            marginTop: lineIndex > 0 ? "4px" : "0",
                            wordBreak: "break-word",
                            overflowWrap: "break-word"
                          }}
                        >
                          {linkifyText(line).map((part, index) =>
                            part.type === "link" ? (
                              <a
                                key={index}
                                href={part.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  color: "var(--mmp-accent)",
                                  textDecoration: "underline",
                                  wordBreak: "break-word",
                                }}
                              >
                                {part.content}
                              </a>
                            ) : (
                              <span key={index}>{part.content}</span>
                            ),
                          )}
                        </div>
                      ));
                    })()}
                  </div>

                  {/* See more / See less button for original post */}
                  {(() => {
                    const contentText = localPost.original_post.content;
                    const hasLongContent =
                      getVisibleTextLength(contentText) > 150 ||
                      contentText.split(/\r?\n/).length > 3;

                    return (
                      hasLongContent && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsOriginalPostExpanded(!isOriginalPostExpanded);
                          }}
                          style={{
                            color: "var(--mmp-accent)",
                            cursor: "pointer",
                            fontWeight: 500,
                            marginTop: "4px",
                            fontSize: "14px",
                          }}
                        >
                          {isOriginalPostExpanded ? "See less" : "See more"}
                        </div>
                      )
                    );
                  })()}
                </>
              )}
              {/* Original Post Images */}
              {localPost.original_post.images &&
                localPost.original_post.images.length > 0 && (
                  <div
                    style={{
                      marginTop: "12px",
                      marginBottom: "12px",
                      position: "relative",
                    }}
                  >
                    {localPost.original_post.images.length === 1 ? (
                      <div style={{ position: "relative" }}>
                        <button
                          className="image-zoom-btn"
                          onClick={(e) => {
                            console.log(
                              "Original post single image zoom clicked!",
                            );
                            e.preventDefault();
                            e.stopPropagation();
                            setOriginalZoomImageIndex(0);
                            setShowOriginalImageZoom(true);
                          }}
                          title="Click to zoom"
                          style={{
                            zIndex: 1000,
                            pointerEvents: "auto",
                          }}
                        >
                          <ZoomIn size={20} />
                        </button>
                        <Image
                          key={resolvedTheme}
                          src={`${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${localPost.original_post.images[0].image_path}`}
                          fluid
                          style={{
                            borderRadius: "8px",
                            maxHeight: "500px",
                            width: "100%",
                            objectFit: "contain",
                            backgroundColor: "var(--mmp-hover-bg)",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToPostDetails();
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              imageNotAvailableSrc;
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{ position: "relative" }}>
                        <button
                          className="image-zoom-btn"
                          onClick={(e) => {
                            console.log("Original post carousel zoom clicked!");
                            e.preventDefault();
                            e.stopPropagation();
                            setOriginalZoomImageIndex(0);
                            setShowOriginalImageZoom(true);
                          }}
                          title="Click to zoom"
                          style={{
                            zIndex: 1000,
                            pointerEvents: "auto",
                          }}
                        >
                          <ZoomIn size={20} />
                        </button>
                        <Carousel
                          slide={false}
                          indicators={false}
                          interval={null}
                          touch={true}
                          className="w-100"
                          style={{ borderRadius: "8px", overflow: "hidden" }}
                          onSelect={(selectedIndex) =>
                            setOriginalZoomImageIndex(selectedIndex)
                          }
                        >
                          {localPost.original_post.images.map((img, idx) => (
                            <Carousel.Item key={idx}>
                              <Image
                                key={resolvedTheme}
                                src={`${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`}
                                style={{
                                  width: "100%",
                                  height: "400px",
                                  objectFit: "cover",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToPostDetails();
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    imageNotAvailableSrc;
                                }}
                              />
                            </Carousel.Item>
                          ))}
                        </Carousel>
                      </div>
                    )}
                  </div>
                )}
              {/* Original Post Poll */}
              {localPost.original_post.poll && (
                <div onClick={(e) => e.stopPropagation()}>
                  <PollWidget poll={localPost.original_post.poll} readOnly />
                </div>
              )}
            </div>
          )}
          {/* Reaction Summary - HYBRID: Shows both web reactions + legacy likes */}
          <div className="reaction-card">
            {(() => {
              const totalReactions = Object.values(
                localPost.reactions || {},
              ).reduce((sum, count) => sum + count, 0);
              const legacyLikesCount = Math.max(
                0,
                localPost.likes - totalReactions,
              );
              const allReactionsCount = totalReactions + legacyLikesCount;
              const hasContent =
                allReactionsCount > 0 ||
                localPost.comments > 0 ||
                localPost.shares > 0;

              if (!hasContent) return null;

              return (
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center reaction-container">
                    {(() => {
                      const totalReactions = Object.values(
                        localPost.reactions || {},
                      ).reduce((sum, count) => sum + count, 0);
                      const currentReactionTotal = totalReactions;
                      const legacyLikesCount = Math.max(
                        0,
                        localPost.likes - currentReactionTotal,
                      );
                      const allReactionsCount =
                        totalReactions + legacyLikesCount;

                      // Don't show anything if there are no reactions at all
                      if (allReactionsCount === 0) {
                        return null;
                      }

                      const hasUserReaction = localPost.userReaction;

                      // If only user reacted (count is 1 and user has reacted)
                      if (allReactionsCount === 1 && hasUserReaction) {
                        // Get the emoji icon for the user's reaction
                        const reactionEmojis = {
                          like: (
                            <ThumbsUp
                              height={22}
                              fill={
                                localPost.userReaction ? "currentColor" : "none"
                              }
                            />
                          ),
                          love: (
                            <img
                              src={loveImg}
                              alt="love"
                              width={24}
                              height={22}
                            />
                          ),
                          haha: (
                            <img
                              src={hahaImg}
                              alt="haha"
                              width={24}
                              height={22}
                            />
                          ),
                          wow: (
                            <img
                              src={wowImg}
                              alt="wow"
                              width={24}
                              height={22}
                            />
                          ),
                          sad: (
                            <img
                              src={sadImg}
                              alt="sad"
                              width={24}
                              height={22}
                            />
                          ),
                          angry: (
                            <img
                              src={angryImg}
                              alt="angry"
                              width={24}
                              height={22}
                            />
                          ),
                        };

                        return (
                          <div className="d-flex align-items-center gap-1">
                            <div
                              className={`d-flex align-items-center gap-2 action-btn ${
                                localPost.userReaction
                                  ? "text-primary"
                                  : "text-muted"
                              }`}
                            >
                              {" "}
                              {reactionEmojis[hasUserReaction]}
                            </div>
                            <span style={{ fontSize: "14px" }}>You</span>
                          </div>
                        );
                      }

                      // Otherwise show FacebookCounter for multiple reactions
                      return (
                        <div
                          onClick={() => setShowReactionsModal(true)}
                          style={{ cursor: "pointer" }}
                        >
                          <FacebookCounter
                            counters={(() => {
                              const order = [
                                "like",
                                "love",
                                "haha",
                                "wow",
                                "sad",
                                "angry",
                              ];
                              const list = [];
                              const counts = localPost.reactions || {};

                              // Calculate how many legacy likes exist (from mobile)
                              const currentReactionTotal = Object.values(
                                counts,
                              ).reduce((sum, count) => sum + count, 0);
                              const legacyLikesCount = Math.max(
                                0,
                                localPost.likes - currentReactionTotal,
                              );

                              // Add legacy likes as 'like' reactions (they show as thumbs up)
                              for (let i = 0; i < legacyLikesCount; i++) {
                                list.push({
                                  emoji: "like",
                                  by: `legacy-like-${i}`,
                                });
                              }

                              // Add web reactions
                              order.forEach((type) => {
                                const n = Math.max(0, counts[type] || 0);
                                for (let i = 0; i < n; i++) {
                                  if (
                                    localPost.userReaction === type &&
                                    !list.some(
                                      (e) => e.by === "You" && e.emoji === type,
                                    )
                                  ) {
                                    list.push({ emoji: type, by: "You" });
                                  } else {
                                    list.push({
                                      emoji: type,
                                      by: `user-${type}-${i}`,
                                    });
                                  }
                                }
                              });
                              return list;
                            })()}
                            user={"You"}
                            bg={"var(--mmp-surface)"}
                            alwaysShowOthers={true}
                            size={24}
                          />
                        </div>
                      );
                    })()}
                  </div>
                  <div className="d-flex align-items-center comments-counter">
                    {localPost.comments > 0 && (
                      <span
                        onClick={navigateToPostDetailsFromComment}
                        style={{ cursor: "pointer" }}
                      >
                        {localPost.comments} comments
                      </span>
                    )}
                    {localPost.shares > 0 && (
                      <span>{localPost.shares} shares</span>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Post Actions */}
          <div className="post-actions">
            <div className="d-flex align-items-center justify-content-between">
              <div
                className="d-flex align-items-center actions-content"
                style={{ position: "relative" }}
              >
                {/* Like Button with Reaction Picker */}
                <div
                  className="position-relative"
                  onMouseEnter={
                    !localPost._optimistic ? handleReactionsEnter : null
                  }
                  onMouseLeave={
                    !localPost._optimistic ? handleReactionsLeave : null
                  }
                >
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleLike}
                    disabled={localPost._optimistic}
                    data-tour={cardIndex === 0 ? "cm-pc-like" : undefined}
                    className={`d-flex align-items-center gap-2 action-btn ${
                      localPost.userReaction ? "text-primary" : "text-muted"
                    }`}
                    style={{ opacity: localPost._optimistic ? 0.5 : 1 }}
                    title={
                      localPost._optimistic
                        ? "Please wait for post to finish uploading..."
                        : ""
                    }
                  >
                    <span style={{ height: "22px", fontSize: "22px" }}>
                      {localPost.userReaction ? (
                        reactionIcons[localPost.userReaction] || (
                          <ThumbsUp size={22} />
                        )
                      ) : (
                        <ThumbsUp size={22} />
                      )}
                    </span>
                    {(() => {
                      const totalReactions = Object.values(
                        localPost.reactions || {},
                      ).reduce((sum, c) => sum + c, 0);
                      const totalLikes = Math.max(
                        localPost.likes || 0,
                        totalReactions,
                      );
                      return totalLikes > 0 ? <span>{totalLikes}</span> : null;
                    })()}
                  </Button>

                  {showReactionPicker && !localPost._optimistic && (
                    <div
                      className="reaction-picker-popup"
                      style={{
                        position: "absolute",
                        bottom: "100%",
                        left: 16,
                        zIndex: 1000,
                        marginBottom: 8,
                      }}
                      onMouseEnter={handleReactionsEnter}
                      onMouseLeave={handleReactionsLeave}
                    >
                      <FacebookSelector onSelect={handleReactionPick} />
                    </div>
                  )}
                </div>

                {/* Comment Button */}
                <Button
                  variant="link"
                  size="sm"
                  onClick={navigateToPostDetailsFromComment}
                  data-tour={cardIndex === 0 ? "cm-pc-comment" : undefined}
                  className="d-flex align-items-center gap-2 action-btn text-muted"
                >
                  <MessageSquare size={22} strokeWidth={2} />
                  {localPost.comments > 0 && (
                    <span
                      onClick={navigateToPostDetailsFromComment}
                      style={{ cursor: "pointer" }}
                    >
                      {localPost.comments}
                    </span>
                  )}
                  {/* <span>Comment</span> */}
                </Button>

                {/* Share Button */}
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleShareClick}
                  data-tour={cardIndex === 0 ? "cm-pc-share" : undefined}
                  className="d-flex align-items-center gap-2 action-btn text-muted"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: 22, height: 22 }}
                  >
                    <path d="m22 2-7 20-4-9-9-4 20-7z" />
                  </svg>
                  {/* <span>Share</span> */}
                </Button>

                {/* RePost Button — hidden on the Saved tab via hideRepost. */}
                {!localPost?.is_repost && !hideRepost && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleRepostClick}
                    className="d-flex align-items-center gap-2 action-btn text-muted"
                  >
                    <Repeat2 size={24} />
                    {/* <span>RePost</span> */}
                  </Button>
                )}
              </div>

              <div className="d-flex align-items-center actions-right">
                {/* Add Comment CTA — shown on desktop only */}
                {/* <Button
                  type="button"
                  size="sm"
                  variant="link"
                  className="add-comment-btn"
                  onClick={navigateToPostDetailsFromComment}
                >
                  Add Comment
                </Button> */}

                {/* Save Button */}
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleSaveClick}
                  disabled={saveLoading || localPost._optimistic}
                  data-tour={cardIndex === 0 ? "cm-pc-save" : undefined}
                  className={`d-flex align-items-center gap-2 action-btn ${
                    isSaved ? "text-primary" : "text-muted"
                  }`}
                  style={{ opacity: localPost._optimistic ? 0.5 : 1 }}
                >
                  <Bookmark
                    size={24}
                    fill={isSaved ? "currentColor" : "none"}
                  />
                  {/* <span>Save</span> */}
                </Button>

                {!isOwnPost && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 action-info"
                    aria-label="Report"
                    onClick={handleReportClick}
                  >
                    <Info size={24} />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Commented Users Indicator - Facebook Style */}
          {localPost.comments > 0 && (
            <div
              className="post-action-description"
              onClick={navigateToPostDetailsFromComment}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex align-items-center">
                {/* Show up to 3 user avatars (only if we have them) */}
                {localPost.commentedUsers &&
                  localPost.commentedUsers.length > 0 && (
                    <div
                      className="d-flex align-items-center"
                      style={{ marginRight: "8px" }}
                    >
                      {localPost.commentedUsers.slice(0, 3).map(
                        (user, index) =>
                          user && (
                            <Image
                              key={index}
                              src={`${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${user.image}`}
                              alt={user.name}
                              roundedCircle
                              width={24}
                              height={24}
                              className="border border-white"
                              style={{
                                marginLeft: index > 0 ? "-8px" : "0",
                                zIndex: 3 - index,
                                objectFit: "cover",
                              }}
                              title={user.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  user.name || "User",
                                )}&background=0195D3&color=fff&size=24`;
                              }}
                            />
                          ),
                      )}
                    </div>
                  )}

                {/* Text description - Facebook/LinkedIn style (always show based on total count) */}
                <small className="action-desc">
                  {(() => {
                    const users = (localPost.commentedUsers || []).filter(
                      Boolean,
                    );
                    const total = localPost.comments;

                    // If no user data but we have count
                    if (users.length === 0) {
                      if (total === 0) return null;
                      if (total === 1) return "1 comment";
                      return `${total.toLocaleString()} comments`;
                    }

                    // Check if current user is in the list
                    const currentUserIndex = users.findIndex(
                      (u) => u.name === "You",
                    );
                    const hasCurrentUser = currentUserIndex !== -1;

                    // Build names array, prioritizing current user (Facebook/LinkedIn always shows "You" first)
                    let displayUsers = [...users];
                    if (hasCurrentUser) {
                      displayUsers = [
                        users[currentUserIndex],
                        ...users.filter((_, i) => i !== currentUserIndex),
                      ];
                    }

                    const firstName = displayUsers[0]?.name || "Someone";
                    const secondName = displayUsers[1]?.name;

                    // Format large numbers (1000+ as 1K, 1000000+ as 1M)
                    const formatCount = (num) => {
                      if (num >= 1000000)
                        return `${(num / 1000000)
                          .toFixed(1)
                          .replace(/\.0$/, "")}M`;
                      if (num >= 1000)
                        return `${(num / 1000)
                          .toFixed(1)
                          .replace(/\.0$/, "")}K`;
                      return num.toLocaleString();
                    };

                    if (total === 1) {
                      return `${firstName} commented`;
                    }

                    if (total === 2 && secondName) {
                      // Show both names only when we have exactly 2 comments and both user names
                      return `${firstName} and ${secondName} commented`;
                    }

                    // For 3+ or when we don't have second name for 2 comments
                    const othersCount = total - 1;
                    return `${firstName} and ${formatCount(othersCount)} ${
                      othersCount === 1 ? "other" : "others"
                    } commented`;
                  })()}
                </small>
              </div>
            </div>
          )}
        </div>
        {/* Comments Section */}
        {showComments && (
          <CommentsSection
            postId={localPost.id}
            onCommentCountUpdate={handleCommentUpdate}
          />
        )}
        {/* Share Modal */}
        {shareModal && (
          <div
            className="modal-backdrop-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              zIndex: 99998,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={closeShareModal}
          >
            <div
              className="modal-content-wrapper"
              style={{
                backgroundColor: "var(--mmp-surface)",
                borderRadius: "12px",
                maxWidth: "400px",
                width: "90%",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                zIndex: 99999,
                position: "relative",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px",
                  borderBottom: "1px solid var(--mmp-border)",
                }}
              >
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "var(--mmp-text-primary)",
                    margin: 0,
                    lineHeight: "24px",
                  }}
                >
                  Share Post
                </h3>
                <button
                  onClick={closeShareModal}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    borderRadius: "4px",
                    color: "var(--mmp-text-secondary)",
                    fontSize: "24px",
                    lineHeight: 1,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: "0" }}>
                {/* Twitter */}
                <div
                  onClick={() => shareToSocialMedia("twitter")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 24px",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--mmp-border)",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "#1da1f2",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    <FaXTwitter size={15} />
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    Share on Twitter
                  </span>
                </div>

                {/* Facebook */}
                <div
                  onClick={() => shareToSocialMedia("facebook")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 24px",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--mmp-border)",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "#1877f2",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    <FaFacebookF size={15} />
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    Share on Facebook
                  </span>
                </div>

                {/* WhatsApp */}
                <div
                  onClick={() => shareToSocialMedia("whatsapp")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 24px",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--mmp-border)",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "#25d366",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    <FaWhatsapp size={15} />
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    Share on WhatsApp
                  </span>
                </div>

                {/* LinkedIn */}
                <div
                  onClick={() => shareToSocialMedia("linkedin")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 24px",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--mmp-border)",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "#0077b5",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    <FaLinkedinIn size={15} />
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    Share on LinkedIn
                  </span>
                </div>

                {/* Instagram */}
                <div
                  onClick={() => shareToSocialMedia("instagram")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 24px",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--mmp-border)",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      background:
                        "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    <FaInstagram size={15} />
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    Share on Instagram
                  </span>
                </div>

                {/* Copy Link */}
                <div
                  onClick={() => shareToSocialMedia("copy")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 24px",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    borderBottomLeftRadius: "12px",
                    borderBottomRightRadius: "12px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <Copy
                    style={{ width: "20px", height: "20px", color: "var(--mmp-text-secondary)" }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    Copy Link
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Article Detail Modal */}
        {showArticle && (
          <ArticleDetailModal
            show={showArticle}
            post={localPost}
            onClose={closeArticle}
            onLike={handleLike}
            onShare={onShare}
            onCommentCountUpdate={handleCommentUpdate}
          />
        )}
        {/* Unfollow Confirmation Modal — same structure & classes as FollowingModal */}
        <Modal
          show={showUnfollowModal}
          onHide={cancelUnfollow}
          centered
          size="sm"
          backdrop="static"
          dialogClassName="custom-modal profile-other-details-modal unfollow-confirmation-modal"
        >
          {showUnfollowModal && (
            <div
              className="modal-backdrop-blur"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(2px)",
                WebkitBackdropFilter: "blur(2px)",
                zIndex: 1040,
                pointerEvents: "none",
              }}
            />
          )}
          <div
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              position: "relative",
              zIndex: 1050,
            }}
          >
            <Modal.Header>
              <h2 className="modal-title">Unfollow User</h2>
              <Button className="close-btn" onClick={cancelUnfollow}>
                <FiX size={24} />
              </Button>
            </Modal.Header>
            <Modal.Body>
              <div className="text-center">
                <Image
                  src={
                    localPost.authorImage
                      ? `${process.env.REACT_APP_API_BASE_URL}/${
                          localPost.authorImage.startsWith(
                            "uploads/user_profile/",
                          )
                            ? localPost.authorImage
                            : `uploads/user_profile/${localPost.authorImage}`
                        }`
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          localPost.author || "User",
                        )}&background=0195D3&color=fff&size=60`
                  }
                  alt={localPost.author || "User"}
                  roundedCircle
                  width={60}
                  height={60}
                  className="mb-2"
                  style={{ objectFit: "cover" }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      localPost.author || "User",
                    )}&background=0195D3&color=fff&size=60`;
                  }}
                />
                <h6 className="mb-1">{localPost.author}</h6>
                {localPost?._metadata?.userState && (
                  <p className="text-muted small mb-2">
                    {localPost._metadata.userState}
                  </p>
                )}
                <p className="small">
                  Are you sure you want to unfollow this user?
                </p>
              </div>

              <div className="text-center mt-4">
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={confirmUnfollow}
                  disabled={unfollowLoading}
                  style={{ fontSize: "12px", padding: "4px 12px" }}
                >
                  {unfollowLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Unfollowing...
                    </>
                  ) : (
                    "Unfollow"
                  )}
                </Button>
              </div>
            </Modal.Body>
          </div>
        </Modal>
        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteModal}
          // onHide={cancelDeletePost}
          onHide={onHide}
          centered
          // className="delete-post-modal"
          dialogClassName="custom-modal delete-post"
        >
          {/* <Modal.Header>
            <Button className="close-btn" onClick={onHide}>
              <FiX size={24} />
            </Button>
          </Modal.Header> */}
          <Modal.Body>
            <h3 className="title pt-0">Delete Post</h3>
            <p className="mb-0">
              Are you sure you want to delete this post? This action cannot be
              undone and will remove all comments and likes associated with it.
            </p>
            <div className="buttons text-end">
              <Button onClick={cancelDeletePost} className="btn btn-secondary">
                Cancel
              </Button>
              <Button onClick={confirmDeletePost} className="btn btn-danger">
                Delete
              </Button>
            </div>
          </Modal.Body>
        </Modal>
        {/* Report Post Modal */}
        {/* <Modal
          show={showReportModal}
          onHide={cancelReportPost}
          centered
          dialogClassName="custom-modal report-post"
        >
          <Modal.Header>
            <h5 className="modal-title">Report Post</h5>
          </Modal.Header>
          <Modal.Body className="pt-0">
            <div style={{ marginBottom: "20px" }}>
              {[
                "Spam",
                "Nudity",
                "Scam",
                "Hate Speech",
                "Harassment",
                "Violence",
                "Misinformation",
                "Terrorism",
                "Illegal Activity",
                "Child Exploitation",
              ].map((reason) => (
                <div
                  key={reason}
                  onClick={() => handleReportReasonChange(reason)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 0",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--mmp-border)",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: `2px solid ${
                        selectedReportReason === reason ? "var(--mmp-accent)" : "var(--mmp-border)"
                      }`,
                      marginRight: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {selectedReportReason === reason && (
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: "var(--mmp-accent)",
                        }}
                      />
                    )}
                  </div>
                  <span style={{ fontSize: "16px", color: "var(--mmp-text-primary)" }}>
                    {reason}
                  </span>
                </div>
              ))}
            </div>
            <div className="buttons text-end">
              <Button onClick={cancelReportPost} className="btn btn-secondary">
                Cancel
              </Button>
              <Button onClick={confirmReportPost} className="btn btn-primary">
                Submit
              </Button>
            </div>
          </Modal.Body>
        </Modal> */}
        <Modal
          show={showReportModal}
          onHide={cancelReportPost}
          centered
          size="lg"
          dialogClassName="custom-modal profile-other-details-modal"
        >
          <Modal.Header>
            <h5 className="modal-title">Report Post</h5>
            <Button className="close-btn" onClick={cancelReportPost}>
              <FiX size={24} />
            </Button>
          </Modal.Header>

          <Modal.Body className="pt-0">
            <div className="radio-options">
              <div className="row">
                {reportReasons.map((reason) => (
                  <div
                    key={reason}
                    className={`radio-option col-12 col-md-6 ${
                      selectedReportReason === reason ? "selected" : ""
                    }`}
                    onClick={() => handleReportReasonChange(reason)}
                  >
                    <div className="contentwrap">
                      <input
                        type="radio"
                        name="reportReason"
                        id={reason}
                        value={reason}
                        checked={selectedReportReason === reason}
                        readOnly
                      />
                      <label htmlFor={reason} className="radio-label">
                        {reason}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="buttons text-center mt-2">
              <Button
                variant="primary"
                onClick={confirmReportPost}
                disabled={!selectedReportReason}
              >
                Submit <ChevronRightIcon className="button-icon" />
              </Button>
            </div>
          </Modal.Body>
        </Modal>
        {/* Image Zoom Modal */}
        <ImageZoomModal
          show={showImageZoom}
          onClose={() => setShowImageZoom(false)}
          images={localPost.post_images?.map(
            (img) =>
              `${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`,
          )}
          currentIndex={zoomImageIndex}
        />

        {/* Original Post Image Zoom Modal */}
        {localPost.is_repost && localPost.original_post?.images && (
          <ImageZoomModal
            show={showOriginalImageZoom}
            onClose={() => setShowOriginalImageZoom(false)}
            images={localPost.original_post.images.map(
              (img) =>
                `${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`,
            )}
            currentIndex={originalZoomImageIndex}
          />
        )}
        {/* Repost Modal */}
        <Modal
          show={showRepostModal}
          onHide={handleRepostCancel}
          centered
          size="lg"
          backdrop="static"
          dialogClassName="custom-modal logout-modal subscription-action-modal"
        >
          <Modal.Header>
            <h5 className="modal-title">Confirm Repost</h5>
            <Button className="close-btn" onClick={handleRepostCancel}>
              <FiX size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body>
            <div className="action-content mt-1">
              <strong>Are you sure you want to repost this post?</strong>
            </div>
            <div className="actions">
              <Button
                className="btn btn-secondary"
                onClick={handleRepostCancel}
              >
                Cancel
              </Button>
              <Button className="btn btn-primary" onClick={handleRepostSubmit}>
                Repost Now
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        {/* User Profile Modal */}
        <UserProfileModal
          show={showUserProfileModal}
          onHide={() => setShowUserProfileModal(false)}
          userId={localPost._metadata?.userId}
          userName={localPost.author}
          userImage={localPost.authorImage}
          userState={localPost._metadata?.userState}
        />

        {/* Original Post User Profile Modal */}
        <UserProfileModal
          show={showOriginalUserProfileModal}
          onHide={() => setShowOriginalUserProfileModal(false)}
          userId={localPost.original_post?.user?.id}
          userName={localPost.original_post?.user?.name}
          userImage={localPost.original_post?.user?.image}
          userState={localPost.original_post?.user?.state}
        />

        {/* Reaction User Profile Modal */}
        <UserProfileModal
          show={showReactionUserProfileModal}
          onHide={() => {
            setShowReactionUserProfileModal(false);
            setSelectedReactionUser(null);
          }}
          userId={selectedReactionUser?.id}
          userName={selectedReactionUser?.user_name}
          userImage={selectedReactionUser?.profile_photo_path}
          userState={selectedReactionUser?.state}
        />

        {/* Follow / profile restriction modal for non-pro users */}
        <RestrictionModal
          isVisible={showPaywallSheet}
          onClose={() => setShowPaywallSheet(false)}
          eventSource="follow_limit"
          iconType="user-follow"
          heading="Want to follow or chat with this user?"
          subText="Upgrade to connect and build real relationships."
          infoText="Pro users can follow, chat, and connect with anyone in the community."
        />

        {/* Save / bookmark restriction modal for non-pro users */}
        <RestrictionModal
          isVisible={showSaveRestriction}
          onClose={() => setShowSaveRestriction(false)}
          eventSource="save_limit"
          iconType="bookmark"
          heading="Want to save this post?"
          subText="Upgrade to Pro to bookmark posts and find them later."
          infoText="Pro users can save unlimited community posts to revisit anytime."
        />

        {/* Repost restriction modal for non-pro users */}
        <RestrictionModal
          isVisible={showRepostRestriction}
          onClose={() => setShowRepostRestriction(false)}
          eventSource="repost_limit"
          iconType="repost"
          heading="Want to repost this?"
          subText="Upgrade to Pro to share posts with your network."
          infoText="Pro users can repost community content to amplify conversations."
        />

        {/* Reactions Modal */}
        <Modal
          show={showReactionsModal}
          onHide={() => setShowReactionsModal(false)}
          centered
          size="md"
          dialogClassName="custom-modal"
        >
          <Modal.Header
            style={{
              padding: "20px 24px 10px",
              border: "none",
            }}
          >
            <h5
              className="modal-title"
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "var(--mmp-text-primary)",
                margin: 0,
              }}
            >
              Reactions
            </h5>
            <Button
              className="close-btn"
              onClick={() => setShowReactionsModal(false)}
              variant="link"
              style={{
                background: "none",
                border: "none",
                padding: "0",
                color: "var(--mmp-text-secondary)",
              }}
            >
              <FiX size={24} />
            </Button>
          </Modal.Header>

          <Modal.Body
            style={{
              padding: "10px 24px 24px",
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {(() => {
              // Get liked users from the post data
              const likedUsers =
                localPost.liked_users || post?.liked_users || [];

              if (likedUsers && likedUsers.length > 0) {
                return (
                  <div className="reactions-list">
                    {likedUsers.map((user, index) => (
                      <div
                        key={`${user.id}-${index}`}
                        className="d-flex align-items-center justify-content-between"
                        style={{
                          transition: "background-color 0.2s ease",
                          borderRadius: "8px",
                          margin: "0 -8px",
                          padding: "12px 8px",
                          borderBottom:
                            index < likedUsers.length - 1
                              ? "1px solid var(--mmp-border)"
                              : "none",
                          cursor:
                            user.id !== currentUserId ? "pointer" : "default",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <div
                          className="d-flex align-items-center"
                          onClick={() => handleReactionUserClick(user)}
                          style={{
                            cursor:
                              user.id !== currentUserId ? "pointer" : "default",
                            flex: 1,
                          }}
                        >
                          <Image
                            src={
                              user.profile_photo_path
                                ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${user.profile_photo_path}`
                                : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                    user.user_name || "User",
                                  )}&background=0195D3&color=fff&size=40`
                            }
                            alt={user.user_name || "User"}
                            roundedCircle
                            width={40}
                            height={40}
                            className="me-3"
                            style={{
                              objectFit: "cover",
                              cursor:
                                user.id !== currentUserId
                                  ? "pointer"
                                  : "default",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                user.user_name || "User",
                              )}&background=0195D3&color=fff&size=40`;
                            }}
                          />
                          <div>
                            <div
                              className="fw-medium"
                              // style={{
                              //   fontSize: "14px",
                              //   color:
                              //     user.id !== currentUserId
                              //       ? "#0195D3"
                              //       : "#333",
                              //   cursor:
                              //     user.id !== currentUserId
                              //       ? "pointer"
                              //       : "default",
                              // }}
                              style={{
                                fontSize: "14px",
                                color: "var(--mmp-text-primary)",
                                cursor:
                                  user.id !== getUser()?.id
                                    ? "pointer"
                                    : "default",
                                transition: "color 0.3s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (user.id !== getUser()?.id) {
                                  e.target.style.color = "var(--mmp-accent)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.color = "var(--mmp-text-primary)";
                              }}
                            >
                              {user.user_name || "Unknown User"}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "12px" }}
                            >
                              {user.reacted_at
                                ? new Date(user.reacted_at).toLocaleDateString()
                                : ""}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center">
                          {user.reaction_type && (
                            <div
                              className="d-flex align-items-center gap-1"
                              title={
                                user.reaction_type.charAt(0).toUpperCase() +
                                user.reaction_type.slice(1)
                              }
                            >
                              {user.reaction_type === "like" && (
                                <ThumbsUp
                                  height={20}
                                  fill="currentColor"
                                  style={{ color: "#1877f2" }}
                                />
                              )}
                              {user.reaction_type === "love" && (
                                <img
                                  src={loveImg}
                                  alt="love"
                                  width={20}
                                  height={20}
                                />
                              )}
                              {user.reaction_type === "haha" && (
                                <img
                                  src={hahaImg}
                                  alt="haha"
                                  width={20}
                                  height={20}
                                />
                              )}
                              {user.reaction_type === "wow" && (
                                <img
                                  src={wowImg}
                                  alt="wow"
                                  width={20}
                                  height={20}
                                />
                              )}
                              {user.reaction_type === "sad" && (
                                <img
                                  src={sadImg}
                                  alt="sad"
                                  width={20}
                                  height={20}
                                />
                              )}
                              {user.reaction_type === "angry" && (
                                <img
                                  src={angryImg}
                                  alt="angry"
                                  width={20}
                                  height={20}
                                />
                              )}
                              {/* <span
                                style={{ fontSize: "12px", color: "#6c757d" }}
                              >
                                {user.reaction_type.charAt(0).toUpperCase() +
                                  user.reaction_type.slice(1)}
                              </span> */}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              } else {
                return (
                  <div className="text-center py-4 text-muted">
                    <p>No reactions yet</p>
                  </div>
                );
              }
            })()}
          </Modal.Body>
        </Modal>
      </Card>
    </>
  );
};

export default PostCard;
